/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Key, Plus, Copy, CheckCircle, Trash2,
  Calendar, Users, TrendingUp, Eye, EyeOff, RefreshCw, FileText
} from 'lucide-react';
import { useInviteCodes, useInviteCodeStats, useCreateInviteCode, useDisableInviteCode } from '@/hooks/api/useInviteCodes';
import { InviteCode, InviteCodeStatus, CreateInviteCodeRequest } from '@/types/superadmin.types';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { SearchInput } from '@/components/ui';
import { InviteCodeDetailModal } from '@/components/superadmin/InviteCodeDetailModal';

// Dialog state type
interface DisableDialogState {
  isOpen: boolean;
  code: InviteCode | null;
}

// Status Badge
const StatusBadge: React.FC<{
  status: InviteCodeStatus;
  t: (key: string) => string;
}> = ({ status, t }) => {
  const styles = {
    ACTIVE: 'bg-emerald-500/15 text-emerald-500',
    EXPIRED: 'bg-gray-500/15 text-gray-400',
    EXHAUSTED: 'bg-amber-500/15 text-amber-500',
    DISABLED: 'bg-red-500/15 text-red-500',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status]}`}>
      {t(`inviteCodes.status.${status.toLowerCase()}`)}
    </span>
  );
};

// Code Display Component
const CodeDisplay: React.FC<{ code: string }> = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(true);

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-sm text-foreground">
        {visible ? code : '••••••••••'}
      </span>
      <button
        onClick={() => setVisible(!visible)}
        className="p-1 rounded hover:bg-hover text-muted hover:text-foreground"
      >
        {visible ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
      </button>
      <button
        onClick={copyCode}
        className="p-1 rounded hover:bg-hover text-muted hover:text-foreground"
      >
        {copied ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
      </button>
    </div>
  );
};

// Skeleton Row Component
const SkeletonRow: React.FC = () => (
  <div className="codes-table-row grid grid-cols-[150px_1.5fr_1fr_100px_100px_80px_80px] gap-4 px-4 py-3 items-center border-b border-border-subtle animate-pulse">
    <div className="h-4 w-24 bg-muted/20 rounded"></div>
    <div className="h-4 w-32 bg-muted/20 rounded col-description"></div>
    <div className="h-4 w-24 bg-muted/20 rounded col-partner"></div>
    <div className="h-4 w-16 bg-muted/20 rounded col-uses"></div>
    <div className="h-5 w-16 bg-muted/20 rounded-full"></div>
    <div className="h-4 w-10 bg-muted/20 rounded col-rate"></div>
    <div className="w-7 h-7 bg-muted/20 rounded"></div>
  </div>
);

// Stat Card Skeleton
const StatCardSkeleton: React.FC = () => (
  <div className="bg-surface border border-border-subtle rounded-xl p-4 animate-pulse">
    <div className="w-10 h-10 rounded-lg bg-muted/20 mb-2"></div>
    <div className="h-6 w-12 bg-muted/20 rounded mb-1"></div>
    <div className="h-3 w-20 bg-muted/20 rounded"></div>
  </div>
);

// Create Code Modal
const CreateCodeModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: CreateInviteCodeRequest) => void;
  isPending: boolean;
  t: (key: string) => string;
}> = ({ isOpen, onClose, onCreate, isPending, t }) => {
  const [form, setForm] = useState({
    prefix: 'VCX',
    description: '',
    maxUses: '',
    expiresInDays: '30',
    commissionRate: '10',
  });

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({
      prefix: form.prefix,
      description: form.description,
      maxUses: form.maxUses ? parseInt(form.maxUses) : undefined,
      expiresInDays: form.expiresInDays ? parseInt(form.expiresInDays) : undefined,
      commissionRate: form.commissionRate ? parseInt(form.commissionRate) : 0,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-surface border border-border-subtle rounded-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <Key className="w-5 h-5" /> {t('inviteCodes.createModal.title')}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-muted">{t('inviteCodes.createModal.codePrefix')}</label>
            <input
              type="text"
              value={form.prefix}
              onChange={(e) => setForm(prev => ({ ...prev, prefix: e.target.value.toUpperCase() }))}
              className="w-full mt-1 h-10 px-3 bg-background border border-border-subtle rounded-lg text-sm text-foreground focus:outline-none"
              placeholder="VCX"
              maxLength={10}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted">{t('inviteCodes.createModal.description')}</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full mt-1 h-10 px-3 bg-background border border-border-subtle rounded-lg text-sm text-foreground focus:outline-none"
              placeholder={t('inviteCodes.createModal.descriptionPlaceholder')}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted">{t('inviteCodes.createModal.maxUses')}</label>
              <input
                type="number"
                value={form.maxUses}
                onChange={(e) => setForm(prev => ({ ...prev, maxUses: e.target.value }))}
                className="w-full mt-1 h-10 px-3 bg-background border border-border-subtle rounded-lg text-sm text-foreground focus:outline-none"
                placeholder="100"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted">{t('inviteCodes.createModal.expiresIn')}</label>
              <input
                type="number"
                value={form.expiresInDays}
                onChange={(e) => setForm(prev => ({ ...prev, expiresInDays: e.target.value }))}
                className="w-full mt-1 h-10 px-3 bg-background border border-border-subtle rounded-lg text-sm text-foreground focus:outline-none"
                placeholder="30"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted">{t('inviteCodes.createModal.commissionRate')}</label>
            <input
              type="number"
              value={form.commissionRate}
              onChange={(e) => setForm(prev => ({ ...prev, commissionRate: e.target.value }))}
              className="w-full mt-1 h-10 px-3 bg-background border border-border-subtle rounded-lg text-sm text-foreground focus:outline-none"
              placeholder="10"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border-subtle rounded-lg text-sm font-medium text-foreground hover:bg-hover"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? t('inviteCodes.createModal.creating') : t('inviteCodes.createModal.generate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Invite Codes Page
export default function InviteCodesPage() {
  const { t } = useTranslation('platform');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [disableDialog, setDisableDialog] = useState<DisableDialogState>({ isOpen: false, code: null });

  // Detail modal state
  const [selectedCode, setSelectedCode] = useState<InviteCode | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Use the invite codes hook
  const { data, isLoading, isFetching, refetch } = useInviteCodes({
    page: currentPage,
    size: 20,
  });

  // Use stats hook
  const { data: stats, isLoading: statsLoading } = useInviteCodeStats();

  // Mutations
  const createMutation = useCreateInviteCode();
  const disableMutation = useDisableInviteCode();

  const codes = data?.content || [];
  const totalElements = data?.page?.totalElements || 0;

  // Filter codes by search query (client-side for now)
  const filteredCodes = useMemo(() => {
    if (!searchQuery) return codes;
    const query = searchQuery.toLowerCase();
    return codes.filter(c =>
      c.code.toLowerCase().includes(query) ||
      c.description?.toLowerCase().includes(query) ||
      c.partnerName?.toLowerCase().includes(query)
    );
  }, [codes, searchQuery]);

  const handleCreateCode = (data: CreateInviteCodeRequest) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        setShowModal(false);
        refetch();
      },
    });
  };

  const handleDelete = (code: InviteCode) => {
    setDisableDialog({ isOpen: true, code });
  };

  // Handle view code detail
  const handleViewDetail = (code: InviteCode) => {
    setSelectedCode(code);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailModalOpen(false);
    setSelectedCode(null);
  };

  const handleDisableConfirm = () => {
    if (!disableDialog.code) return;
    disableMutation.mutate(disableDialog.code.id, {
      onSuccess: () => {
        setDisableDialog({ isOpen: false, code: null });
        refetch();
      },
      onError: () => {
        setDisableDialog({ isOpen: false, code: null });
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Key className="w-6 h-6" /> {t('inviteCodes.title')}
          </h1>
          <p className="text-sm text-muted">{t('inviteCodes.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 rounded-lg border border-border hover:bg-hover disabled:opacity-50"
            title={t('common.refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" /> {t('inviteCodes.generateCode')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {statsLoading || isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center mb-2">
                <Key className="w-5 h-5 text-emerald-500" />
              </div>
              <div className="font-mono text-3xl font-bold text-foreground">{stats?.activeCount || 0}</div>
              <div className="text-xs text-muted">{t('inviteCodes.stats.activeCodes')}</div>
            </div>
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center mb-2">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div className="font-mono text-3xl font-bold text-foreground">{stats?.totalRedemptions || 0}</div>
              <div className="text-xs text-muted">{t('inviteCodes.stats.totalRedemptions')}</div>
            </div>
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <div className="w-10 h-10 rounded-lg bg-purple-500/15 flex items-center justify-center mb-2">
                <TrendingUp className="w-5 h-5 text-purple-500" />
              </div>
              <div className="font-mono text-3xl font-bold text-foreground">{stats?.partnerCodes || 0}</div>
              <div className="text-xs text-muted">{t('inviteCodes.stats.partnerCodes')}</div>
            </div>
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center mb-2">
                <Calendar className="w-5 h-5 text-amber-500" />
              </div>
              <div className="font-mono text-3xl font-bold text-foreground">{stats?.expiredCount || 0}</div>
              <div className="text-xs text-muted">{t('inviteCodes.stats.expiredCodes')}</div>
            </div>
          </>
        )}
      </div>

      {/* Search */}
      <SearchInput
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder={t('inviteCodes.searchPlaceholder')}
        className="max-w-md"
      />

      {/* Table */}
      <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
        <div className="codes-table-header grid grid-cols-[150px_1.5fr_1fr_100px_100px_80px_80px] gap-4 px-4 py-3 bg-background border-b border-border-subtle">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">{t('inviteCodes.table.code')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-description">{t('inviteCodes.table.description')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-partner">{t('inviteCodes.table.partner')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-uses">{t('inviteCodes.table.uses')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">{t('inviteCodes.table.status')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-rate">{t('inviteCodes.table.rate')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">{t('inviteCodes.table.actions')}</div>
        </div>

        {isLoading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : filteredCodes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted">
            <Key className="w-12 h-12 mb-3 opacity-50" />
            <p>{t('inviteCodes.noCodes')}</p>
          </div>
        ) : (
          filteredCodes.map((code) => (
            <div
              key={code.id}
              className="codes-table-row grid grid-cols-[150px_1.5fr_1fr_100px_100px_80px_80px] gap-4 px-4 py-3 items-center border-b border-border-subtle last:border-b-0 hover:bg-hover"
            >
              <div><CodeDisplay code={code.code} /></div>
              <div className="text-base text-secondary truncate col-description">{code.description || '-'}</div>
              <div className="text-base text-foreground col-partner">{code.partnerName || '-'}</div>
              <div className="text-base text-foreground col-uses">
                {code.currentUses}{code.maxUses ? ` / ${code.maxUses}` : ''}
              </div>
              <div><StatusBadge status={code.status} t={t} /></div>
              <div className="font-mono text-base text-foreground col-rate">
                {code.commissionRate > 0 ? `${code.commissionRate}%` : '-'}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleViewDetail(code)}
                  className="p-1.5 rounded hover:bg-hover transition-colors text-muted hover:text-foreground"
                  title={t('inviteCodes.actions.viewDetails')}
                >
                  <FileText className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(code)}
                  className="p-1.5 rounded hover:bg-red-500/15 text-muted hover:text-red-500 transition-colors disabled:opacity-50"
                  disabled={code.status !== 'ACTIVE' || disableMutation.isPending}
                  title={t('inviteCodes.actions.disableCode')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Results count */}
      {!isLoading && totalElements > 0 && (
        <div className="text-sm text-muted">
          {t('inviteCodes.showingResults', { showing: filteredCodes.length, total: totalElements })}
        </div>
      )}

      {/* Create Modal */}
      <CreateCodeModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onCreate={handleCreateCode}
        isPending={createMutation.isPending}
        t={t}
      />

      {/* Disable Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={disableDialog.isOpen}
        variant="danger"
        title={t('inviteCodes.disableDialog.title')}
        message={t('inviteCodes.disableDialog.message')}
        details={disableDialog.code && (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted">{t('inviteCodes.disableDialog.code')}:</span>
              <span className="font-mono font-medium">{disableDialog.code.code}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{t('inviteCodes.disableDialog.description')}:</span>
              <span className="font-medium">{disableDialog.code.description || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{t('inviteCodes.disableDialog.uses')}:</span>
              <span className="font-medium">
                {disableDialog.code.currentUses}{disableDialog.code.maxUses ? ` / ${disableDialog.code.maxUses}` : ''}
              </span>
            </div>
          </div>
        )}
        confirmText={t('inviteCodes.disableDialog.confirm')}
        isLoading={disableMutation.isPending}
        onConfirm={handleDisableConfirm}
        onCancel={() => setDisableDialog({ isOpen: false, code: null })}
      />

      {/* Invite Code Detail Modal */}
      <InviteCodeDetailModal
        inviteCode={selectedCode}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetail}
      />

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .codes-table-header,
          .codes-table-row {
            grid-template-columns: 1fr 80px 60px !important;
          }
          .col-description,
          .col-partner,
          .col-uses,
          .col-rate {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
